import { Injectable } from '@angular/core';

/**
 * Image metadata information
 */
export interface ImageMetadata {
  width: number;
  height: number;
  dpi: number | null;
  colorProfile: string | null;
  fileSize: number;
}

/**
 * Camera EXIF data extracted from photos
 */
export interface PhotoExifData {
  camera?: string;
  lens?: string;
  focalLength?: string;
  aperture?: string;
  shutterSpeed?: string;
  iso?: string;
  dateTaken?: string;
  fileSize?: number;
}

/**
 * Service to extract metadata from image files
 */
@Injectable({
  providedIn: 'root'
})
export class ImageMetadataService {

  /**
   * Extract metadata from an image file
   */
  async getImageMetadata(file: File): Promise<ImageMetadata> {
    const metadata: ImageMetadata = {
      width: 0,
      height: 0,
      dpi: null,
      colorProfile: null,
      fileSize: file.size
    };

    try {
      // Get image dimensions
      const dimensions = await this.getImageDimensions(file);
      metadata.width = dimensions.width;
      metadata.height = dimensions.height;

      // Try to extract DPI and color profile from EXIF/file headers
      const arrayBuffer = await file.arrayBuffer();
      const dataView = new DataView(arrayBuffer);

      // Check for JPEG
      if (file.type === 'image/jpeg' || file.name.toLowerCase().endsWith('.jpg') || file.name.toLowerCase().endsWith('.jpeg')) {
        const jpegMetadata = this.extractJpegMetadata(dataView);
        metadata.dpi = jpegMetadata.dpi;
        metadata.colorProfile = jpegMetadata.colorProfile;
      }
      // Check for PNG
      else if (file.type === 'image/png' || file.name.toLowerCase().endsWith('.png')) {
        const pngMetadata = this.extractPngMetadata(dataView);
        metadata.dpi = pngMetadata.dpi;
        metadata.colorProfile = pngMetadata.colorProfile;
      }
      // Check for TIFF
      else if (file.type === 'image/tiff' || file.name.toLowerCase().endsWith('.tif') || file.name.toLowerCase().endsWith('.tiff')) {
        const tiffMetadata = this.extractTiffMetadata(dataView);
        metadata.dpi = tiffMetadata.dpi;
        metadata.colorProfile = tiffMetadata.colorProfile;
      }

    } catch (error) {
      console.error('Error extracting image metadata:', error);
    }

    return metadata;
  }

  /**
   * Get image dimensions by loading it
   */
  private getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.width, height: img.height });
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  }

  /**
   * Extract metadata from JPEG file
   */
  private extractJpegMetadata(dataView: DataView): { dpi: number | null; colorProfile: string | null } {
    let dpi: number | null = null;
    let colorProfile: string | null = null;

    try {
      // Check for JPEG SOI marker (0xFFD8)
      if (dataView.getUint16(0, false) !== 0xFFD8) {
        return { dpi, colorProfile };
      }

      let offset = 2;
      const length = dataView.byteLength;

      // Scan for APP markers
      while (offset < length) {
        const marker = dataView.getUint16(offset, false);
        offset += 2;

        // APP0 (JFIF) marker - contains DPI
        if (marker === 0xFFE0) {
          const segmentLength = dataView.getUint16(offset, false);
          
          // Check for JFIF identifier
          const identifier = String.fromCharCode(
            dataView.getUint8(offset + 2),
            dataView.getUint8(offset + 3),
            dataView.getUint8(offset + 4),
            dataView.getUint8(offset + 5)
          );

          if (identifier === 'JFIF') {
            const densityUnits = dataView.getUint8(offset + 9);
            const xDensity = dataView.getUint16(offset + 10, false);
            const yDensity = dataView.getUint16(offset + 12, false);

            // Units: 0=no units, 1=dots per inch, 2=dots per cm
            if (densityUnits === 1 && xDensity > 0) {
              dpi = xDensity;
            } else if (densityUnits === 2 && xDensity > 0) {
              dpi = Math.round(xDensity * 2.54); // Convert cm to inches
            }
          }

          offset += segmentLength;
        }
        // APP1 (EXIF) marker - also contains DPI and color space info
        else if (marker === 0xFFE1) {
          const segmentLength = dataView.getUint16(offset, false);
          
          // Check for EXIF identifier
          const identifier = String.fromCharCode(
            dataView.getUint8(offset + 2),
            dataView.getUint8(offset + 3),
            dataView.getUint8(offset + 4),
            dataView.getUint8(offset + 5)
          );

          if (identifier === 'Exif') {
            // Try to extract DPI from EXIF if not found in JFIF
            const exifDpi = this.extractExifDpi(dataView, offset + 8);
            if (exifDpi && !dpi) {
              dpi = exifDpi;
            }
            
            // Try to extract color space from EXIF
            const exifColorSpace = this.extractExifColorSpace(dataView, offset + 8);
            if (exifColorSpace && !colorProfile) {
              colorProfile = exifColorSpace;
            }
          }

          offset += segmentLength;
        }
        // APP2 (ICC Profile) marker
        else if (marker === 0xFFE2) {
          const segmentLength = dataView.getUint16(offset, false);
          
          // Check for ICC_PROFILE identifier
          const identifier = String.fromCharCode(
            dataView.getUint8(offset + 2),
            dataView.getUint8(offset + 3),
            dataView.getUint8(offset + 4),
            dataView.getUint8(offset + 5),
            dataView.getUint8(offset + 6),
            dataView.getUint8(offset + 7),
            dataView.getUint8(offset + 8),
            dataView.getUint8(offset + 9),
            dataView.getUint8(offset + 10),
            dataView.getUint8(offset + 11),
            dataView.getUint8(offset + 12)
          );

          if (identifier === 'ICC_PROFILE') {
            // Try to read profile description from ICC profile
            const iccOffset = offset + 2 + 14; // Skip segment header + ICC_PROFILE\0
            if (iccOffset + 80 < offset + segmentLength) {
              // ICC Profile header structure:
              // Offset 16-19: Color space (RGB, CMYK, GRAY, etc)
              // Offset 20-23: Profile connection space (XYZ, Lab)
              // The profile description is in the tag table
              
              // Read color space signature first
              const colorSpace = String.fromCharCode(
                dataView.getUint8(iccOffset + 16),
                dataView.getUint8(iccOffset + 17),
                dataView.getUint8(iccOffset + 18),
                dataView.getUint8(iccOffset + 19)
              ).trim();
              
              // Try to find and read the profile description tag (0x64657363 = 'desc')
              // Tag table starts at offset 128
              const tagCount = dataView.getUint32(iccOffset + 128, false);
              let profileDescription = '';
              
              for (let i = 0; i < Math.min(tagCount, 50); i++) {
                const tagTableOffset = iccOffset + 128 + 4 + (i * 12);
                if (tagTableOffset + 12 > dataView.byteLength) break;
                
                const tagSignature = dataView.getUint32(tagTableOffset, false);
                const tagOffset = dataView.getUint32(tagTableOffset + 4, false);
                const tagSize = dataView.getUint32(tagTableOffset + 8, false);
                
                // Check for 'desc' tag (0x64657363)
                if (tagSignature === 0x64657363) {
                  profileDescription = this.readIccDescription(dataView, iccOffset + tagOffset);
                  break;
                }
              }
              
              console.log('[ImageMetadata] ICC Profile - Color Space:', colorSpace, 'Description:', profileDescription);
              
              // Determine color profile from description or color space
              if (profileDescription) {
                const desc = profileDescription.toLowerCase();
                if (desc.includes('srgb') || desc.includes('sRGB')) {
                  colorProfile = 'sRGB';
                } else if (desc.includes('adobe rgb') || desc.includes('adobergb')) {
                  colorProfile = 'Adobe RGB (1998)';
                } else if (desc.includes('prophoto') || desc.includes('pro photo')) {
                  colorProfile = 'ProPhoto RGB';
                } else if (desc.includes('display p3') || desc.includes('p3')) {
                  colorProfile = 'Display P3';
                } else if (desc.includes('cmyk')) {
                  colorProfile = 'CMYK';
                } else if (desc.includes('gray') || desc.includes('grey')) {
                  colorProfile = 'Grayscale';
                } else {
                  // Show the actual profile name if available
                  colorProfile = profileDescription.substring(0, 30); // Limit length
                }
              } else {
                // Fallback to color space if no description
                if (colorSpace === 'RGB') {
                  colorProfile = 'Perfil Incompatível';
                } else if (colorSpace === 'CMYK') {
                  colorProfile = 'CMYK';
                } else if (colorSpace === 'GRAY') {
                  colorProfile = 'Grayscale';
                } else {
                  colorProfile = 'Perfil Incompatível';
                }
              }
            } else {
              colorProfile = 'Perfil Incompatível';
            }
          }

          offset += segmentLength;
        }
        // Start of Scan - no more headers
        else if (marker === 0xFFDA) {
          break;
        }
        // Other markers
        else if ((marker & 0xFF00) === 0xFF00) {
          const segmentLength = dataView.getUint16(offset, false);
          offset += segmentLength;
        } else {
          break;
        }
      }

      // Don't default to sRGB - leave null if not found
      // Don't default to 300 DPI - leave null if not found

    } catch (error) {
      console.error('Error parsing JPEG metadata:', error);
    }

    // If no color profile was detected, mark as incompatible
    if (!colorProfile) {
      colorProfile = 'Perfil Incompatível';
    }

    return { dpi, colorProfile };
  }

  /**
   * Read an ICC profile's description. ICC v2 profiles store it as the ASCII
   * `desc` (textDescriptionType) layout; v4 profiles use `mluc`
   * (multiLocalizedUnicodeType, UTF-16BE records) — phone JPEGs embedding a
   * v4 "sRGB IEC61966-2.1" were unreadable before and fell through to the
   * "Perfil Incompatível" fallback despite being genuinely sRGB.
   */
  private readIccDescription(dataView: DataView, descOffset: number): string {
    let description = '';
    try {
      if (descOffset + 12 > dataView.byteLength) return '';
      const tagType = dataView.getUint32(descOffset, false);

      if (tagType === 0x64657363) { // 'desc' — textDescriptionType (ICC v2)
        const descLength = dataView.getUint32(descOffset + 8, false);
        if (descLength > 0 && descLength < 200) {
          for (let j = 0; j < descLength - 1; j++) {
            const char = dataView.getUint8(descOffset + 12 + j);
            if (char >= 32 && char < 127) {
              description += String.fromCharCode(char);
            }
          }
        }
      } else if (tagType === 0x6D6C7563) { // 'mluc' — multiLocalizedUnicodeType (ICC v4)
        const recordCount = dataView.getUint32(descOffset + 8, false);
        if (recordCount > 0) {
          // First record: string length + offset in bytes (offset relative
          // to the tag start), text encoded as UTF-16BE.
          const stringLength = dataView.getUint32(descOffset + 20, false);
          const stringOffset = dataView.getUint32(descOffset + 24, false);
          const start = descOffset + stringOffset;
          if (stringLength > 0 && stringLength < 400 && start + stringLength <= dataView.byteLength) {
            for (let j = 0; j + 1 < stringLength; j += 2) {
              const code = dataView.getUint16(start + j, false);
              if (code >= 32 && code < 127) {
                description += String.fromCharCode(code);
              }
            }
          }
        }
      }
    } catch {
      // Unreadable description — caller falls back to the header color space
    }
    return description;
  }

  /**
   * Extract DPI from EXIF data
   */
  private extractExifDpi(dataView: DataView, exifOffset: number): number | null {
    try {
      // Check TIFF byte order
      const byteOrder = dataView.getUint16(exifOffset, false);
      const littleEndian = byteOrder === 0x4949; // 'II'
      
      if (!littleEndian && byteOrder !== 0x4D4D) { // 'MM'
        return null;
      }

      // Get IFD offset
      const ifdOffset = exifOffset + dataView.getUint32(exifOffset + 4, littleEndian);
      const numEntries = dataView.getUint16(ifdOffset, littleEndian);

      // Look for XResolution (tag 282) and ResolutionUnit (tag 296)
      let xResolution: number | null = null;
      let resolutionUnit = 2; // Default to inches

      for (let i = 0; i < numEntries; i++) {
        const entryOffset = ifdOffset + 2 + i * 12;
        const tag = dataView.getUint16(entryOffset, littleEndian);
        const type = dataView.getUint16(entryOffset + 2, littleEndian);
        
        if (tag === 282) { // XResolution
          const valueOffset = dataView.getUint32(entryOffset + 8, littleEndian);
          const absOffset = exifOffset + valueOffset;
          if (type === 5 && absOffset < dataView.byteLength - 8) { // RATIONAL
            const numerator = dataView.getUint32(absOffset, littleEndian);
            const denominator = dataView.getUint32(absOffset + 4, littleEndian);
            if (denominator > 0) {
              xResolution = numerator / denominator;
            }
          }
        } else if (tag === 296) { // ResolutionUnit
          resolutionUnit = dataView.getUint16(entryOffset + 8, littleEndian);
        }
      }

      if (xResolution) {
        // ResolutionUnit: 2=inches, 3=centimeters
        if (resolutionUnit === 3) {
          return Math.round(xResolution * 2.54); // Convert cm to inches
        }
        return Math.round(xResolution);
      }
    } catch (error) {
      console.error('Error extracting EXIF DPI:', error);
    }
    return null;
  }

  /**
   * Extract color space from EXIF data
   */
  private extractExifColorSpace(dataView: DataView, exifOffset: number): string | null {
    try {
      // Check TIFF byte order
      const byteOrder = dataView.getUint16(exifOffset, false);
      const littleEndian = byteOrder === 0x4949; // 'II'

      if (!littleEndian && byteOrder !== 0x4D4D) { // 'MM'
        return null;
      }

      // The ColorSpace tag (0xA001) lives in the Exif SUB-IFD (pointed to by
      // IFD0 tag 0x8769) per the EXIF spec — scanning only IFD0 never found
      // it, which flagged profile-less sRGB JPEGs (ICC not embedded, sRGB
      // declared via EXIF) as "Perfil Incompatível". Scan IFD0 first to
      // tolerate non-standard writers, then follow the pointer.
      const ifd0Offset = exifOffset + dataView.getUint32(exifOffset + 4, littleEndian);
      const fromIfd0 = this.findColorSpaceInIfd(dataView, ifd0Offset, littleEndian);
      if (fromIfd0) return fromIfd0;

      const exifIfdPointer = this.findIfdTagValue32(dataView, ifd0Offset, 0x8769, littleEndian);
      if (exifIfdPointer) {
        return this.findColorSpaceInIfd(dataView, exifOffset + exifIfdPointer, littleEndian);
      }
    } catch (error) {
      console.error('Error extracting EXIF color space:', error);
    }
    return null;
  }

  /** Scan one IFD for the ColorSpace tag (0xA001) and map its value. */
  private findColorSpaceInIfd(dataView: DataView, ifdOffset: number, littleEndian: boolean): string | null {
    if (ifdOffset + 2 > dataView.byteLength) return null;
    const numEntries = dataView.getUint16(ifdOffset, littleEndian);
    for (let i = 0; i < numEntries; i++) {
      const entryOffset = ifdOffset + 2 + i * 12;
      if (entryOffset + 12 > dataView.byteLength) break;
      if (dataView.getUint16(entryOffset, littleEndian) !== 0xA001) continue;
      const colorSpace = dataView.getUint16(entryOffset + 8, littleEndian);
      // 1 = sRGB, 2 = Adobe RGB, 65535 = Uncalibrated
      console.log('[ImageMetadata] EXIF ColorSpace tag value:', colorSpace);
      if (colorSpace === 1) {
        return 'sRGB';
      }
      if (colorSpace === 2) {
        return 'Adobe RGB (1998)';
      }
      if (colorSpace === 65535) {
        // Uncalibrated - likely Adobe RGB
        return 'Adobe RGB (1998)';
      }
      return null;
    }
    return null;
  }

  /** Read the 32-bit value of a tag in an IFD (e.g. the 0x8769 Exif-IFD pointer). */
  private findIfdTagValue32(dataView: DataView, ifdOffset: number, wantedTag: number, littleEndian: boolean): number | null {
    if (ifdOffset + 2 > dataView.byteLength) return null;
    const numEntries = dataView.getUint16(ifdOffset, littleEndian);
    for (let i = 0; i < numEntries; i++) {
      const entryOffset = ifdOffset + 2 + i * 12;
      if (entryOffset + 12 > dataView.byteLength) break;
      if (dataView.getUint16(entryOffset, littleEndian) === wantedTag) {
        return dataView.getUint32(entryOffset + 8, littleEndian);
      }
    }
    return null;
  }

  /**
   * Extract metadata from PNG file
   */
  private extractPngMetadata(dataView: DataView): { dpi: number | null; colorProfile: string | null } {
    let dpi: number | null = null;
    let colorProfile: string | null = null;

    try {
      // Check PNG signature
      if (dataView.getUint32(0, false) !== 0x89504E47 || dataView.getUint32(4, false) !== 0x0D0A1A0A) {
        return { dpi, colorProfile };
      }

      let offset = 8;
      const length = dataView.byteLength;

      while (offset < length) {
        const chunkLength = dataView.getUint32(offset, false);
        offset += 4;

        const chunkType = String.fromCharCode(
          dataView.getUint8(offset),
          dataView.getUint8(offset + 1),
          dataView.getUint8(offset + 2),
          dataView.getUint8(offset + 3)
        );
        offset += 4;

        // pHYs chunk - physical pixel dimensions
        if (chunkType === 'pHYs') {
          const pixelsPerUnitX = dataView.getUint32(offset, false);
          const pixelsPerUnitY = dataView.getUint32(offset + 4, false);
          const unit = dataView.getUint8(offset + 8);

          // Unit: 0=unknown, 1=meter
          if (unit === 1) {
            // Convert pixels per meter to DPI
            dpi = Math.round(pixelsPerUnitX / 39.3701); // 1 meter = 39.3701 inches
          }
        }
        // iCCP chunk - embedded ICC profile
        else if (chunkType === 'iCCP') {
          colorProfile = 'Adobe RGB (1998)'; // Simplified - proper parsing would read the actual profile
        }
        // sRGB chunk
        else if (chunkType === 'sRGB') {
          colorProfile = 'sRGB';
        }
        // IEND chunk - end of file
        else if (chunkType === 'IEND') {
          break;
        }

        offset += chunkLength + 4; // Skip chunk data + CRC
      }

      // Don't set defaults - leave null if not found

    } catch (error) {
      console.error('Error parsing PNG metadata:', error);
    }

    // If no color profile was detected, mark as incompatible
    if (!colorProfile) {
      colorProfile = 'Perfil Incompatível';
    }

    return { dpi, colorProfile };
  }

  /**
   * Extract metadata from TIFF file
   */
  private extractTiffMetadata(dataView: DataView): { dpi: number | null; colorProfile: string | null } {
    let dpi: number | null = null;
    let colorProfile: string | null = null;

    try {
      // Check TIFF byte order
      const byteOrder = dataView.getUint16(0, false);
      const littleEndian = byteOrder === 0x4949; // 'II'
      const bigEndian = byteOrder === 0x4D4D;    // 'MM'

      if (!littleEndian && !bigEndian) {
        return { dpi, colorProfile };
      }

      // Check TIFF magic number (42)
      const magic = dataView.getUint16(2, littleEndian);
      if (magic !== 42) {
        return { dpi, colorProfile };
      }

      // Get IFD offset
      let ifdOffset = dataView.getUint32(4, littleEndian);

      // Read IFD entries
      const numEntries = dataView.getUint16(ifdOffset, littleEndian);
      ifdOffset += 2;

      for (let i = 0; i < numEntries; i++) {
        const entryOffset = ifdOffset + i * 12;
        const tag = dataView.getUint16(entryOffset, littleEndian);

        // Tag 282: XResolution
        // Tag 283: YResolution
        if (tag === 282 || tag === 283) {
          const type = dataView.getUint16(entryOffset + 2, littleEndian);
          const valueOffset = dataView.getUint32(entryOffset + 8, littleEndian);

          if (type === 5) { // RATIONAL type
            const numerator = dataView.getUint32(valueOffset, littleEndian);
            const denominator = dataView.getUint32(valueOffset + 4, littleEndian);
            dpi = Math.round(numerator / denominator);
          }
        }
        // Tag 262: PhotometricInterpretation
        else if (tag === 262) {
          const value = dataView.getUint16(entryOffset + 8, littleEndian);
          if (value === 2) {
            colorProfile = 'RGB';
          } else if (value === 5) {
            colorProfile = 'CMYK';
          } else if (value === 1 || value === 0) {
            colorProfile = 'Grayscale';
          }
        }
      }

      // Don't set defaults - leave null if not found

    } catch (error) {
      console.error('Error parsing TIFF metadata:', error);
    }

    // If no color profile was detected, mark as incompatible
    if (!colorProfile) {
      colorProfile = 'Perfil Incompatível';
    }

    return { dpi, colorProfile };
  }

  /**
   * Extract camera EXIF data from a JPEG file for display purposes.
   */
  async extractCameraExif(file: File): Promise<PhotoExifData> {
    const exif: PhotoExifData = { fileSize: file.size };
    try {
      const buffer = await file.arrayBuffer();
      const dv = new DataView(buffer);

      // Only JPEG supported for camera EXIF
      if (dv.getUint16(0, false) !== 0xFFD8) return exif;

      let offset = 2;
      while (offset < dv.byteLength) {
        const marker = dv.getUint16(offset, false);
        offset += 2;
        if (marker === 0xFFE1) {
          const segLen = dv.getUint16(offset, false);
          const id = String.fromCharCode(
            dv.getUint8(offset + 2), dv.getUint8(offset + 3),
            dv.getUint8(offset + 4), dv.getUint8(offset + 5)
          );
          if (id === 'Exif') {
            this.parseCameraExifIfd(dv, offset + 8, exif);
          }
          offset += segLen;
        } else if (marker === 0xFFDA) {
          break;
        } else if ((marker & 0xFF00) === 0xFF00) {
          offset += dv.getUint16(offset, false);
        } else {
          break;
        }
      }
    } catch (e) {
      console.error('[ImageMetadata] EXIF extraction error:', e);
    }
    return exif;
  }

  /**
   * Parse IFD0 and Exif sub-IFD for camera metadata.
   */
  private parseCameraExifIfd(dv: DataView, tiffStart: number, exif: PhotoExifData): void {
    const le = dv.getUint16(tiffStart, false) === 0x4949;
    const ifdOff = tiffStart + dv.getUint32(tiffStart + 4, le);
    const count = dv.getUint16(ifdOff, le);

    let exifIfdPointer = 0;

    for (let i = 0; i < count; i++) {
      const entry = ifdOff + 2 + i * 12;
      if (entry + 12 > dv.byteLength) break;
      const tag = dv.getUint16(entry, le);
      const type = dv.getUint16(entry + 2, le);
      const cnt = dv.getUint32(entry + 4, le);

      if (tag === 0x010F) { // Make
        const make = this.readExifString(dv, entry, tiffStart, le, type, cnt);
        if (make) exif.camera = make;
      } else if (tag === 0x0110) { // Model
        const model = this.readExifString(dv, entry, tiffStart, le, type, cnt);
        if (model) {
          exif.camera = exif.camera && !model.startsWith(exif.camera)
            ? exif.camera + ' ' + model : model;
        }
      } else if (tag === 0x8769) { // ExifIFD pointer
        exifIfdPointer = dv.getUint32(entry + 8, le);
      }
    }

    if (exifIfdPointer) {
      this.parseExifSubIfd(dv, tiffStart + exifIfdPointer, tiffStart, le, exif);
    }
  }

  private parseExifSubIfd(
    dv: DataView, ifdOff: number, tiffStart: number, le: boolean, exif: PhotoExifData
  ): void {
    if (ifdOff + 2 > dv.byteLength) return;
    const count = dv.getUint16(ifdOff, le);

    for (let i = 0; i < count; i++) {
      const entry = ifdOff + 2 + i * 12;
      if (entry + 12 > dv.byteLength) break;
      const tag = dv.getUint16(entry, le);
      const type = dv.getUint16(entry + 2, le);
      const cnt = dv.getUint32(entry + 4, le);

      if (tag === 0x829A) { // ExposureTime
        const r = this.readExifRational(dv, entry, tiffStart, le, type);
        if (r) {
          exif.shutterSpeed = r[0] >= r[1] ? `${r[0] / r[1]}s` : `1/${Math.round(r[1] / r[0])}s`;
        }
      } else if (tag === 0x829D) { // FNumber
        const r = this.readExifRational(dv, entry, tiffStart, le, type);
        if (r) exif.aperture = `f/${(r[0] / r[1]).toFixed(1)}`;
      } else if (tag === 0x8827) { // ISOSpeedRatings
        if (type === 3) exif.iso = '' + dv.getUint16(entry + 8, le);
        else if (type === 4) exif.iso = '' + dv.getUint32(entry + 8, le);
      } else if (tag === 0x9003) { // DateTimeOriginal
        const dt = this.readExifString(dv, entry, tiffStart, le, type, cnt);
        if (dt) exif.dateTaken = dt.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3');
      } else if (tag === 0x920A) { // FocalLength
        const r = this.readExifRational(dv, entry, tiffStart, le, type);
        if (r) exif.focalLength = `${Math.round(r[0] / r[1])}mm`;
      } else if (tag === 0xA434) { // LensModel
        const lens = this.readExifString(dv, entry, tiffStart, le, type, cnt);
        if (lens) exif.lens = lens;
      }
    }
  }

  /** Read an ASCII string from an IFD entry. */
  private readExifString(
    dv: DataView, entry: number, tiffStart: number, le: boolean, type: number, count: number
  ): string | null {
    if (type !== 2 || count === 0) return null;
    let off: number;
    if (count <= 4) {
      off = entry + 8;
    } else {
      off = tiffStart + dv.getUint32(entry + 8, le);
    }
    if (off + count > dv.byteLength) return null;
    let s = '';
    for (let j = 0; j < count - 1; j++) {
      const c = dv.getUint8(off + j);
      if (c === 0) break;
      s += String.fromCharCode(c);
    }
    return s.trim() || null;
  }

  /** Read a RATIONAL (type 5) from an IFD entry → [numerator, denominator]. */
  private readExifRational(
    dv: DataView, entry: number, tiffStart: number, le: boolean, type: number
  ): [number, number] | null {
    if (type !== 5 && type !== 10) return null;
    const off = tiffStart + dv.getUint32(entry + 8, le);
    if (off + 8 > dv.byteLength) return null;
    const num = dv.getUint32(off, le);
    const den = dv.getUint32(off + 4, le);
    return den > 0 ? [num, den] : null;
  }
}
